/**
 * Hashtags (`/hashtags`) — vier KPI's, het scrape-blok en de tagtabel.
 * Endpoints: hashtags/stats · hashtags/scrape-status ·
 * hashtags?limit=500&sort_by=post_count&sort_dir=desc
 */
import { useMemo, useState } from 'react'
import { ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { Modal, Field } from '../components/Modal'
import { dt, num } from '../lib/format'

interface Stats {
  total: number
  by_platform: Record<string, number>
  favorites: number
  categories: Record<string, number>
}
interface ScrapeStatus {
  fc: { last_scraped: string | null }
  f2f: { last_scraped: string | null }
  scraper_available: boolean
  running: boolean
  last_error: string | null
}
interface HashtagRow {
  id: number; tag: string; platform: string; post_count: number
  category: string | null; is_favorite: boolean; last_scraped_at: string | null
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

export default function Hashtags() {
  const stats = useApi<Stats>('/api/admin/hashtags/stats')
  const status = useApi<ScrapeStatus>('/api/admin/hashtags/scrape-status')
  const tags = useApi<HashtagRow[]>(
    '/api/admin/hashtags?limit=500&sort_by=post_count&sort_dir=desc')

  const [platform, setPlatform] = useState('all')
  const [favOnly, setFavOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [newPlatform, setNewPlatform] = useState('fc')
  const { run, busy } = useActions()

  const reload = () => { tags.refetch(); stats.refetch() }

  const rows = useMemo(() => {
    let list = tags.data ?? []
    if (platform !== 'all') {
      // de bron gebruikt "fc" en "f2f" als platformwaarde
      const want = platform === 'fancentro' ? 'fc' : 'f2f'
      list = list.filter((t) => t.platform === want || t.platform === platform)
    }
    if (favOnly) list = list.filter((t) => t.is_favorite)
    const needle = search.trim().toLowerCase().replace(/^#/, '')
    if (needle) list = list.filter((t) => t.tag.toLowerCase().includes(needle))
    return list
  }, [tags.data, platform, favOnly, search])

  /** Tags als spatie-gescheiden lijst naar het klembord, zoals de bundle. */
  function copy(list: HashtagRow[]) {
    const text = list.map((t) => (t.tag.startsWith('#') ? t.tag : `#${t.tag}`)).join(' ')
    navigator.clipboard?.writeText(text)
    run({ label: 'Copy', request: async () => {}, successText: `${list.length} tags copied` })
  }

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <input className="inp" style={{ maxWidth: 200 }} placeholder="Filter tags…"
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="chkrow">
          <span className={favOnly ? 'chk on' : 'chk'} onClick={() => setFavOnly((v) => !v)}>
            {favOnly ? '✓' : ''}
          </span>
          Favorites Only
        </label>
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn ghost sm" type="button"
                onClick={() => copy(rows.filter((t) => t.is_favorite))}>Copy Favorites</button>
        <button className="btn ghost sm" type="button"
                onClick={() => copy(rows.slice(0, 30))}>Copy Top 30</button>
      </div>

      <div className="grid kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <Kpi icon="#" label="Total Hashtags" value={num(stats.data?.total)}
             meta={`FC ${num(stats.data?.by_platform.fc)} · F2F ${num(stats.data?.by_platform.f2f)}`} />
        <Kpi icon="♥" label="FanCentro" value={num(stats.data?.by_platform.fc)} meta="tags" />
        <Kpi icon="♦" label="F2F" value={num(stats.data?.by_platform.f2f)} meta="tags" />
        <Kpi icon="★" label="Favorites" value={num(stats.data?.favorites)} meta="starred" />
      </div>

      <Panel title="Scrape Hashtags">
        <div className="row">
          {([['fc', 'Scrape FanCentro'], ['f2f', 'Scrape F2F'], ['both', 'Scrape Both']] as const)
            .map(([plat, label]) => (
              <button
                key={plat}
                className={plat === 'both' ? 'btn sm' : 'btn ghost sm'}
                type="button"
                disabled={!status.data?.scraper_available || busy === label}
                title={status.data?.scraper_available ? undefined : 'Scraper is not available'}
                onClick={() => run({
                  label,
                  request: mut.patch('/api/admin/hashtags/scrape-status', { platform: plat }),
                  onDone: () => { status.refetch(); reload() },
                  successText: `${label} started`,
                })}
              >
                {label}
              </button>
            ))}
          <button className="btn ghost sm" type="button" onClick={() => setAddOpen(true)}>
            + Add manually
          </button>
          <span className="mut2" style={{ fontSize: 12, marginLeft: 'auto' }}>
            FC: {status.data?.fc.last_scraped ? dt(status.data.fc.last_scraped) : '—'} ·
            F2F: {status.data?.f2f.last_scraped ? dt(status.data.f2f.last_scraped) : '—'}
          </span>
        </div>
        {status.data?.running ? (
          <div className="note" style={{ marginTop: 10 }}>Scraper running…</div>
        ) : null}
        {status.data?.last_error ? (
          <div className="note" style={{ marginTop: 10, color: 'var(--bad)' }}>
            {status.data.last_error}
          </div>
        ) : null}
      </Panel>

      <Panel title="Tags" desc={`${num(rows.length)} tags`} flush>
        {tags.loading ? <Loading /> : tags.error ? <ErrorNote error={tags.error} /> : (
          <Table cols={[
            { key: 'sel', label: '' }, { key: 'fav', label: '' },
            { key: 't', label: 'Hashtag' }, { key: 'p', label: 'Platform' },
            { key: 'c', label: 'Posts', num: true }, { key: 'cat', label: 'Category' },
            { key: 'act', label: '' },
          ]} emptyText="No tags match this filter">
            {rows.map((t) => (
              <tr key={t.id}>
                <td><span className="chk" /></td>
                <td>
                  <span
                    style={{ cursor: 'pointer' }}
                    title={t.is_favorite ? 'Uit favorieten' : 'Favoriet maken'}
                    onClick={() => run({
                      label: 'Favorite',
                      request: mut.patch(`/api/admin/hashtags/${t.id}`,
                                         { is_favorite: !t.is_favorite }),
                      onDone: reload,
                      successText: t.is_favorite ? 'Uit favorieten' : 'Favoriet gemaakt',
                    })}
                  >
                    {t.is_favorite ? '★' : <span className="dim">☆</span>}
                  </span>
                </td>
                <td><Tag kind="acc">{t.tag.startsWith('#') ? t.tag : `#${t.tag}`}</Tag></td>
                <td><Plat value={t.platform === 'fc' ? 'fancentro' : t.platform} /></td>
                <td className="num mono">{num(t.post_count)}</td>
                <td className={t.category ? 'mut' : 'dim'}>{t.category || '—'}</td>
                <td>
                  <button
                    className="btn ghost sm"
                    type="button"
                    onClick={() => run({
                      label: 'Delete hashtag',
                      confirm: `Delete ${t.tag}?`,
                      request: mut.del(`/api/admin/hashtags/${t.id}`),
                      onDone: reload,
                    })}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {addOpen ? (
        <Modal
          title="Add hashtag"
          onClose={() => setAddOpen(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                type="button"
                disabled={!newTag.trim() || busy === 'Add hashtag'}
                onClick={() => run({
                  label: 'Add hashtag',
                  request: mut.post('/api/admin/hashtags', {
                    platform: newPlatform,
                    tag: newTag.trim().replace(/^#/, ''),
                    is_favorite: false,
                  }),
                  onDone: () => { setAddOpen(false); setNewTag(''); reload() },
                  successText: 'Hashtag added',
                })}
              >
                Add
              </button>
            </>
          }
        >
          <Field label="Platform">
            <select className="inp" value={newPlatform}
                    onChange={(e) => setNewPlatform(e.target.value)}>
              <option value="fc">FanCentro</option>
              <option value="f2f">F2F</option>
            </select>
          </Field>
          <Field label="Hashtag" hint="With or without # — the # is stripped.">
            <input className="inp" value={newTag} placeholder="#dutch"
                   onChange={(e) => setNewTag(e.target.value)} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}
